import PropTypes from 'prop-types';
import { useContext, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import H2 from '@base/H2/H2';
import P2 from '@base/P2/P2';
import P1 from '@base/P1/P1';
import styled, { ThemeContext } from 'styled-components';
import Drawer from '@base/Drawer/Drawer';
import TextField from '@base/TextField/TextField';
import Button from '@base/Button/Button';
import UnderlineLink from '@comp/UnderlineLink/UnderlineLink';
import { unlockFreePlan, fetchMyInfo } from '@actions/auth';
import imageHelper from '@utils/imageHelper';
import analyticsAction from '@actions/analytics';
import tick from '@images/unlockplan/tick.png';
import curtainGraphic from '@images/unlockplan/curtain-graphic.svg';
import { Spacings, Fonts, TextColors } from '@styles/index';
import {
  sendFreeUnlockDrawerDisplayEvent,
  sendFreePlanUnlockEvent
} from '@events/sendAnalytics';
import { authTypes } from '../../../redux/constants';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(10, ${Spacings.SPACING_8B});
  grid-column-gap: ${Spacings.SPACING_4B};
  position: relative;
`;

const Wrapper = styled.div`
  grid-column: span 10;
`;

const StyledTextField = styled(TextField)`
  margin-top: ${Spacings.SPACING_8B};
  background: ${({ theme }) => theme.colors.white};
  font-weight: ${Fonts.SEMIBOLD};
  font-size: ${Fonts.H6};
  color: ${({ theme }) => theme.colors.fontGrey};
  line-height: ${Fonts.HEADER_LINE_HEIGHT};
`;

const ErrorMsgText = styled(P2)`
  margin-top: ${Spacings.SPACING_2B};
  font-weight: ${Fonts.SEMIBOLD};
`;

const BottomWrap = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${Spacings.SPACING_8B};
`;

const DefaultCodeTextWrapper = styled.div`
  margin-left: auto;
`;

const LinkText = styled(P1)`
  line-height: ${Fonts.BUTTON_LINE_HEIGHT};
  font-weight: ${Fonts.BOLD};
  margin-bottom: ${Spacings.SPACING_1B};
`;

const DefaultCodeText = styled(P2)`
  line-height: ${Fonts.BUTTON_LINE_HEIGHT};
`;

const lightConfettiURL = 'unlockplan/light-mode-confetti.gif';
const darkConfettiURL = 'unlockplan/dark-mode-confetti.gif';

const ConfettiCont = styled.div`
  width: 532px;
  height: 275.17px;
  background-image: ${({ $isDarkModeOn }) =>
    `url(${imageHelper.getImage(
      $isDarkModeOn ? darkConfettiURL : lightConfettiURL
    )})`};
  background-repeat: no-repeat;
  background-position: 50% 40%;
  background-size: cover;
  border-radius: ${Spacings.SPACING_3B};
  position: relative;
`;

const Tick = styled.img`
  user-drag: none;
  user-select: none;
  position: absolute;
  top: 129px;
  left: 233px;
`;

const UnlockedText = styled(H2)`
  text-align: center;
  width: 532px;
`;

const BottomImage = styled.img`
  user-drag: none;
  user-select: none;
  position: absolute;
  right: 0px;
  bottom: 0px;
`;

const LINK_TEXT = "Don't have one?";
const DEFAULT_CODE_PRE_TEXT = 'Try the code ';
const PLACE_HOLDER_TEXT = 'Enter Invite code';
const BUTTON_LABEL_TEXT = 'Unlock';
const UNLOCK_HEADER_TEXT = 'Enter an invite code to unlock your free plan';
const GREEN = 'green';
const FILLED = 'filled';
const DISABLED = 'disabled';
const TEXT = 'text';
const HASH = '#';
const TICK_SIZE = '66px';
const UNLOCKED_MESSAGE = '🙌 Awesome, you have unlocked your free plan!';
const BLANK_STRING = '';
const BOTTOM_IMG_WIDTH = '266px';
const BOTTOM_IMG_HEIGHT = '117px';

const FreePlanUnlock = ({
  drawerState,
  closeDrawerHandler,
  objectType,
  objectUid
}) => {
  const theme = useContext(ThemeContext);
  const dispatch = useDispatch();

  const constants = useSelector(({ others }) => others?.constants);
  const isDarkModeOn = useSelector(({ auth }) => auth?.isDarkModeOn);

  const me = useSelector(({ auth }) => auth.me);
  const goalDetails = useSelector(({ auth }) => auth.selectedGoal);

  const [inviteCode, setInviteCode] = useState(null);
  const [errMsg, setErrMsg] = useState(null);
  const [enableButton, setEnableButton] = useState(false);
  const [showDefaultCode, setShowDefaultCode] = useState(false);
  const [unlockedStat, setUnlockedStat] = useState(false);

  useEffect(() => {
    if (drawerState) {
      sendFreeUnlockDrawerDisplayEvent(
        goalDetails?.uid,
        goalDetails?.name,
        me.uid,
        me.username,
        objectType,
        objectUid
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerState]);

  const handleChange = (evt) => {
    const { value } = evt.target;
    setInviteCode(value.toUpperCase());
    setEnableButton(!!value.length);
  };

  const sendUnlockEvent = (success) => {
    sendFreePlanUnlockEvent(
      goalDetails?.uid,
      goalDetails?.name,
      me.uid,
      me.username,
      objectType,
      objectUid,
      inviteCode,
      success
    );
  };

  const handleUnlock = async () => {
    dispatch(
      analyticsAction(
        'unlock-free-plan',
        '',
        {},
        {
          'Invite Code': inviteCode,
          'Object Type': objectType,
          'Object Id': objectUid
        }
      )
    );
    const payload = {
      referral_code: inviteCode,
      object_type: objectType,
      object_uid: objectUid
    };
    const unlockFreePlanResp = await dispatch(unlockFreePlan(payload));
    if (unlockFreePlanResp?.type === `${authTypes.UNLOCK_FREE_PLAN}_SUCCESS`) {
      sendUnlockEvent(true);
      dispatch(fetchMyInfo());
      setUnlockedStat(true);
    } else if (
      unlockFreePlanResp?.type === `${authTypes.UNLOCK_FREE_PLAN}_FAILURE`
    ) {
      sendUnlockEvent();
      setErrMsg(unlockFreePlanResp?.error?.message);
    }
  };

  const handleShowDefaultCode = (e) => {
    e.preventDefault();
    dispatch(
      analyticsAction(
        'unlock-free-plan-show-code',
        '',
        {},
        {
          'Object Type': objectType,
          'Object Id': objectUid
        }
      )
    );
    setShowDefaultCode(true);
  };

  return (
    <Drawer
      drawerState={drawerState}
      onClose={() => {
        setInviteCode(null);
        setErrMsg(null);
        setEnableButton(false);
        setShowDefaultCode(false);
        closeDrawerHandler();
      }}
    >
      <Grid>
        <Wrapper>
          {unlockedStat ? (
            <>
              <ConfettiCont $isDarkModeOn={isDarkModeOn}>
                <Tick
                  src={tick}
                  width={TICK_SIZE}
                  height={TICK_SIZE}
                  alt={BLANK_STRING}
                />
              </ConfettiCont>
              <UnlockedText color={theme.colors.fontGrey}>
                {UNLOCKED_MESSAGE}
              </UnlockedText>
            </>
          ) : (
            <>
              <H2 color={theme.colors.fontGrey}>{UNLOCK_HEADER_TEXT}</H2>
              <StyledTextField
                type={TEXT}
                value={inviteCode}
                readOnly={false}
                required
                fullWidth
                autoFocus
                placeholder={PLACE_HOLDER_TEXT}
                onChange={handleChange}
              />
              {errMsg && (
                <ErrorMsgText color={theme.colors.red}>{errMsg}</ErrorMsgText>
              )}
              <BottomWrap>
                <Button
                  label={BUTTON_LABEL_TEXT}
                  onClick={handleUnlock}
                  theme={GREEN}
                  variant={enableButton ? FILLED : DISABLED}
                  dataAnalytics="unlock-free-plan"
                />
                <DefaultCodeTextWrapper>
                  {showDefaultCode ? (
                    <>
                      <DefaultCodeText color={TextColors.LIGHT_GREY}>
                        {DEFAULT_CODE_PRE_TEXT}
                        <b>{constants.PLATFORM_UNLOCK_CODE}</b>
                      </DefaultCodeText>
                    </>
                  ) : (
                    <UnderlineLink
                      color={TextColors.LIGHT_GREY}
                      href={HASH}
                      onClick={handleShowDefaultCode}
                    >
                      <LinkText color={TextColors.LIGHT_GREY}>
                        {LINK_TEXT}
                      </LinkText>
                    </UnderlineLink>
                  )}
                </DefaultCodeTextWrapper>
              </BottomWrap>
            </>
          )}
        </Wrapper>
      </Grid>
      {!unlockedStat && (
        <BottomImage
          src={curtainGraphic}
          width={BOTTOM_IMG_WIDTH}
          height={BOTTOM_IMG_HEIGHT}
          alt={BLANK_STRING}
        />
      )}
    </Drawer>
  );
};

FreePlanUnlock.propTypes = {
  closeDrawerHandler: PropTypes.func.isRequired,
  drawerState: PropTypes.bool.isRequired,
  objectType: PropTypes.number.isRequired,
  objectUid: PropTypes.string.isRequired
};

export default FreePlanUnlock;
